import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || ''
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || ''
const backendUrl = process.env.REACT_APP_BACKEND_URL || ''

// Validate environment
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase configuration missing')
}

// Create the Supabase client
// Note: Using default fetch - custom wrappers can cause ReadableStream conflicts on iOS PWA
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false, // Disabled to prevent PWA navigation issues
    flowType: 'pkce',
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
})

// 10s abort signal for interactive writes. Safari web apps (especially on
// MacBooks after wake-from-sleep) can hang a fetch forever on a dead socket —
// this guarantees the request settles so optimistic UI can revert + toast.
// Falls back to no signal on browsers without AbortSignal.timeout (Safari <16).
export const writeTimeoutSignal = () =>
  (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) ? AbortSignal.timeout(10000) : undefined

// Auth functions using backend API to avoid proxy issues
export const signUp = async (email, password, name) => {
  try {
    const response = await fetch(`${backendUrl}/api/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, name }),
    })
    
    const data = await response.json()
    
    if (data.success && data.session?.access_token) {
      // Set the session in Supabase client
      // Wrapped: setSession() can throw ReadableStream errors (iOS PWA)
      // but the backend signup already succeeded at this point
      try {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        })
      } catch (sessionErr) {
        console.warn('[Auth] setSession warning (non-critical):', sessionErr.message)
      }
    }
    
    return {
      data: data.success ? { user: data.user, session: data.session } : null,
      error: data.error ? { message: data.error } : null
    }
  } catch (error) {
    console.error('SignUp error:', error)
    return { data: null, error: { message: error.message || 'Failed to sign up' } }
  }
}

export const signIn = async (email, password) => {
  try {
    const response = await fetch(`${backendUrl}/api/auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    })
    
    const data = await response.json()
    
    if (data.success && data.session?.access_token) {
      // Set the session in Supabase client
      // Wrapped: setSession() can throw ReadableStream errors (iOS PWA)
      // but the backend login already succeeded at this point
      try {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        })
      } catch (sessionErr) {
        console.warn('[Auth] setSession warning (non-critical):', sessionErr.message)
      }
    }
    
    return {
      data: data.success ? { user: data.user, session: data.session } : null,
      error: data.error ? { message: data.error } : null
    }
  } catch (error) {
    console.error('SignIn error:', error)
    return { data: null, error: { message: error.message || 'Failed to sign in' } }
  }
}

export const signOut = async () => {
  try {
    await fetch(`${backendUrl}/api/auth/signout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    await supabase.auth.signOut()
    return { error: null }
  } catch (error) {
    console.error('SignOut error:', error)
    return { error }
  }
}

export const resetPassword = async (email) => {
  try {
    const response = await fetch(`${backendUrl}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })
    
    const data = await response.json()
    return {
      success: data.success,
      error: data.error ? { message: data.error } : null
    }
  } catch (error) {
    console.error('Reset password error:', error)
    return { success: false, error: { message: error.message || 'Failed to send reset email' } }
  }
}

export const getSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession()
    return { data, error }
  } catch (error) {
    console.error('GetSession error:', error)
    return { data: { session: null }, error }
  }
}
